<script>
  import { getContext, onMount } from 'svelte';
  import { initEditor } from './core.js';

  /**
   * @import { Snippet } from 'svelte';
   * @import { TextEditorStore } from '../../typedefs';
   */

  /**
   * @typedef {object} Props
   * @property {string} [class] The `class` attribute on the wrapper element.
   * @property {boolean} [hidden] Whether to hide the widget.
   * @property {boolean} [disabled] Whether to disable the widget. An alias of the `aria-disabled`
   * attribute.
   * @property {boolean} [readonly] Whether to make the widget read-only. An alias of the
   * `aria-readonly` attribute.
   * @property {boolean} [required] Whether to mark the widget required. An alias of the
   * `aria-required` attribute.
   * @property {boolean} [invalid] Whether to mark the widget invalid. An alias of the
   * `aria-invalid` attribute.
   * @property {Snippet} [children] Primary slot content.
   */

  /**
   * @type {Props & Record<string, any>}
   */
  let {
    /* eslint-disable prefer-const */
    class: className,
    hidden = false,
    disabled = false,
    readonly = false,
    required = false,
    invalid = false,
    children,
    ...restProps
    /* eslint-enable prefer-const */
  } = $props();

  /** @type {TextEditorStore} */
  const editorStore = getContext('editorStore');

  /**
   * Reference to the Lexical editor root element.
   * @type {HTMLElement | undefined}
   */
  let lexicalRoot = $state();

  const editable = $derived(!(disabled || readonly));

  $effect(() => {
    editorStore.editor?.setEditable(editable);
  });

  /**
   * Update {@link value} and other state variables whenever the editor content is updated.
   * @param {Event} event `Update` custom event.
   */
  const onUpdate = (event) => {
    const { hasConverterError, useRichText, inputValue } = editorStore;

    if (hasConverterError || !useRichText) {
      return;
    }

    const { value: newValue, selection } = /** @type {CustomEvent} */ (event).detail;

    if (inputValue !== newValue) {
      // Temporarily disable rich text to prevent unnecessary Markdown conversion that resets
      // Lexical nodes and selection, then restore the state
      editorStore.useRichText = false;
      editorStore.inputValue = newValue;
      editorStore.useRichText = true;
    }

    editorStore.selection = selection;
  };

  /**
   * Listen to `click` events on the editor. Ignore a click on a link.
   * @param {MouseEvent} event `click` event.
   */
  const onClick = (event) => {
    if (/** @type {HTMLElement} */ (event.target)?.matches('a')) {
      event.preventDefault();
    }
  };

  onMount(() => {
    editorStore.editor = initEditor(editorStore.config);

    lexicalRoot?.addEventListener('Update', onUpdate);
    lexicalRoot?.addEventListener('click', onClick);

    return () => {
      lexicalRoot?.removeEventListener('Update', onUpdate);
      lexicalRoot?.removeEventListener('click', onClick);
    };
  });

  $effect(() => {
    if (editorStore.editor && lexicalRoot) {
      editorStore.editor.setRootElement(lexicalRoot);
      editorStore.initialized = true;
    }
  });
</script>

<div
  bind:this={lexicalRoot}
  {...restProps}
  role="textbox"
  aria-multiline="true"
  aria-hidden={hidden}
  aria-disabled={disabled}
  aria-readonly={readonly}
  aria-required={required}
  aria-invalid={invalid}
  class="lexical-root"
  class:code={editorStore.config.isCodeEditor}
  id="{editorStore.editorId}-lexical-root"
  contenteditable={editable}
  {hidden}
></div>

<style>.lexical-root {
  overflow: hidden;
  border: 1px solid var(--sui-textbox-border-color);
  border-radius: var(--sui-textbox-border-radius) !important;
  padding: var(--sui-textbox-multiline-padding);
  min-height: 8em;
  color: var(--sui-textbox-foreground-color);
  background-color: var(--sui-textbox-background-color);
  font-family: var(--sui-textbox-font-family);
  font-size: var(--sui-textbox-font-size);
  line-height: var(--sui-textbox-multiline-line-height);
}
.lexical-root:not(:first-child) {
  border-start-start-radius: 0 !important;
  border-start-end-radius: 0 !important;
}
.lexical-root.code {
  padding: 0;
}
.lexical-root.code :global(.code-block) {
  border-radius: 0 !important;
  min-height: 120px;
}
.lexical-root:focus-visible {
  outline: 0;
}
.lexical-root[aria-invalid=true] {
  border-color: var(--sui-error-border-color);
}
.lexical-root :global(:first-child) {
  margin-top: 0;
}
.lexical-root :global(:last-child) {
  margin-bottom: 0;
}
.lexical-root :global(strong.italic) {
  font-style: italic;
}
.lexical-root :global(.strikethrough) {
  text-decoration: line-through;
}
.lexical-root :global(li.nested) {
  list-style-type: none;
}
.lexical-root :global(.code-block) {
  position: relative;
  display: block;
  padding-block: 8px;
  padding-inline-start: 56px;
  padding-inline-end: 8px;
  background-color: var(--sui-code-background-color);
  overflow-x: auto;
  white-space: pre;
}
.lexical-root :global(.code-block:not(:first-child)) {
  margin-top: 1em;
}
.lexical-root :global(.code-block:not(:last-child)) {
  margin-bottom: 1em;
}
.lexical-root :global(.code-block::before) {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  inset-inline-end: auto;
  content: attr(data-gutter);
  padding: 8px;
  min-width: 40px;
  color: var(--sui-tertiary-foreground-color);
  background-color: var(--sui-tertiary-background-color);
  text-align: end;
}
.lexical-root :global([data-lexical-text=true]) {
  cursor: text;
}
.lexical-root :global(:is(th, td)) > :global(p) {
  margin: 0;
  white-space: normal;
  word-break: normal;
}
.lexical-root :global(hr) {
  margin: var(--sui-paragraph-margin) 0;
  border: none;
  padding: 0;
}
.lexical-root :global(hr::after) {
  display: block;
  height: 2px;
  background-color: var(--sui-control-border-color);
  line-height: 2px;
  content: "";
}

:root[data-theme=light] .lexical-root :global(.token:is(.comment, .prolog, .doctype, .cdata)) {
  color: slategray;
}
:root[data-theme=light] .lexical-root :global(.token.punctuation) {
  color: #999;
}
:root[data-theme=light] .lexical-root :global(.token.namespace) {
  opacity: 0.7;
}
:root[data-theme=light] .lexical-root :global(.token:is(.property, .tag, .boolean, .number, .constant, .symbol, .deleted)) {
  color: #905;
}
:root[data-theme=light] .lexical-root :global(.token:is(.selector, .attr-name, .string, .char, .builtin, .inserted)) {
  color: #690;
}
:root[data-theme=light] .lexical-root :global(.token:is(.operator, .entity, .url)), :root[data-theme=light] .lexical-root :global(.language-css) :global(.token.string), :root[data-theme=light] .lexical-root :global(.style) :global(.token.string) {
  color: #9a6e3a;
}
:root[data-theme=light] .lexical-root :global(.token:is(.atrule, .attr-value, .keyword)) {
  color: #07a;
}
:root[data-theme=light] .lexical-root :global(.token:is(.function, .class-name)) {
  color: #dd4a68;
}
:root[data-theme=light] .lexical-root :global(.token:is(.regex, .important, .variable)) {
  color: #e90;
}

:root[data-theme=dark] .lexical-root :global(.token:is(.comment, .block-comment, .prolog, .doctype, .cdata)) {
  color: #999;
}
:root[data-theme=dark] .lexical-root :global(.token.punctuation) {
  color: #ccc;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.tag, .attr-name, .namespace, .deleted)) {
  color: #e2777a;
}
:root[data-theme=dark] .lexical-root :global(.token.function-name) {
  color: #6196cc;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.boolean, .number, .function)) {
  color: #f08d49;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.property, .class-name, .constant, .symbol)) {
  color: #f8c555;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.selector, .important, .atrule, .keyword, .builtin)) {
  color: #cc99cd;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.string, .char, .attr-value, .regex, .variable)) {
  color: #7ec699;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.operator, .entity, .url)) {
  color: #67cdcc;
}
:root[data-theme=dark] .lexical-root :global(.token:is(.important, .bold)) {
  font-weight: bold;
}
:root[data-theme=dark] .lexical-root :global(.token.italic) {
  font-style: italic;
}
:root[data-theme=dark] .lexical-root :global(.token.entity) {
  cursor: help;
}
:root[data-theme=dark] .lexical-root :global(.token.inserted) {
  color: green;
}</style>
